/* Roman → Devanagari transliteration for the night desk's name columns.
 *
 * Guest and applicant names reach us as free text typed in English at booking
 * time — there is no Hindi anywhere in the payload — so "Hindi" here means the
 * same name written in the script a Hindi-reading clerk can take in at a
 * glance, sounded out. It is not translation, and it is deliberately
 * approximate: Roman spellings of Indian names drop vowel length ("Kumar" for
 * कुमार, "Sharma" for शर्मा), so no rule can recover what the writer meant.
 * The desk keeps the English spelling one hover away (see the `title` on the
 * cells) for exactly this reason, and the search box and sorting both still
 * work off the English, which is what a keyboard can type.
 */

/* Titles are recognised whole rather than sounded out — "Mr" letter by letter
   is म्र, which is not a word. */
const HONORIFICS = {
  mr: "श्री",
  shri: "श्री",
  sri: "श्री",
  mrs: "श्रीमती",
  smt: "श्रीमती",
  ms: "सुश्री",
  miss: "कुमारी",
  km: "कुमारी",
  dr: "डॉ.",
  prof: "प्रो.",
};

/* Names common enough that the rules getting them wrong would be noticed every
   night. Roman spelling collapses distinctions Devanagari keeps — one "a"
   stands for both अ and आ, so Kumar is कुमार but Shankar is शंकर, and no rule
   can tell them apart — so the frequent ones are simply listed. Anything not
   here still gets sounded out; add a line when the desk reports a name reading
   wrong. Keys are lowercase, matched whole. */
const WORDS = {
  kumar: "कुमार", kumari: "कुमारी", singh: "सिंह", sharma: "शर्मा",
  verma: "वर्मा", gupta: "गुप्ता", mishra: "मिश्रा", tiwari: "तिवारी",
  pandey: "पांडेय", chauhan: "चौहान", yadav: "यादव", das: "दास",
  devi: "देवी", rani: "रानी", lal: "लाल", prasad: "प्रसाद", nath: "नाथ",
  chandra: "चंद्र", chand: "चंद", mohan: "मोहन", shankar: "शंकर",
  prakash: "प्रकाश", kailash: "कैलाश", ram: "राम", raj: "राज",
  rajesh: "राजेश", ramesh: "रमेश", suresh: "सुरेश", mahesh: "महेश",
  dinesh: "दिनेश", naresh: "नरेश", umesh: "उमेश", ganesh: "गणेश",
  rakesh: "राकेश", mukesh: "मुकेश", ashok: "अशोक", anil: "अनिल",
  sunil: "सुनील", vikas: "विकास", vikash: "विकास", vinod: "विनोद",
  manoj: "मनोज", sanjay: "संजय", ajay: "अजय", vijay: "विजय",
  amit: "अमित", sumit: "सुमित", rohit: "रोहित", mohit: "मोहित",
  deepak: "दीपक", dipak: "दीपक", pankaj: "पंकज", rahul: "राहुल",
  arun: "अरुण", varun: "वरुण", anand: "आनंद", abhijit: "अभिजीत",
  kavita: "कविता", sunita: "सुनीता", anita: "अनीता", meena: "मीना",
  geeta: "गीता", seema: "सीमा", pooja: "पूजा", priya: "प्रिया",
  neha: "नेहा", nisha: "निशा", asha: "आशा", usha: "उषा", rekha: "रेखा",
  radha: "राधा", khan: "खान", ali: "अली", ahmed: "अहमद",
  mohammad: "मोहम्मद", abdul: "अब्दुल",
};

/* Letter names, for initials and acronyms — "HSDPR" is spelled out, not read
   as a word. */
const LETTER_NAMES = {
  a: "ए", b: "बी", c: "सी", d: "डी", e: "ई", f: "एफ", g: "जी", h: "एच",
  i: "आई", j: "जे", k: "के", l: "एल", m: "एम", n: "एन", o: "ओ", p: "पी",
  q: "क्यू", r: "आर", s: "एस", t: "टी", u: "यू", v: "वी", w: "डब्ल्यू",
  x: "एक्स", y: "वाई", z: "ज़ेड",
};

/* Consonants, longest spelling first — the matcher takes the first that fits,
   so "chh" has to be tried before "ch" and "ch" before "c". */
const CONSONANTS = [
  ["chh", "छ"], ["ksh", "क्ष"],
  ["kh", "ख"], ["gh", "घ"], ["ch", "च"], ["jh", "झ"], ["th", "थ"],
  ["dh", "ध"], ["ph", "फ"], ["bh", "भ"], ["sh", "श"], ["ck", "क"],
  ["k", "क"], ["g", "ग"], ["c", "क"], ["j", "ज"], ["t", "त"], ["d", "द"],
  ["n", "न"], ["p", "प"], ["b", "ब"], ["m", "म"], ["y", "य"], ["r", "र"],
  ["l", "ल"], ["v", "व"], ["w", "व"], ["s", "स"], ["h", "ह"], ["f", "फ़"],
  ["z", "ज़"], ["q", "क"], ["x", "क्स"],
];

/* Vowels in both forms: the independent letter when the vowel opens a syllable
   of its own, the matra when it attaches to the consonant just written. "a" is
   the inherent vowel every consonant already carries, so its matra is nothing
   at all. */
const VOWELS = [
  ["aa", "आ", "ा"], ["ai", "ऐ", "ै"], ["au", "औ", "ौ"],
  ["ee", "ई", "ी"], ["ii", "ई", "ी"], ["oo", "ऊ", "ू"], ["uu", "ऊ", "ू"],
  ["a", "अ", ""], ["i", "इ", "ि"], ["e", "ए", "े"],
  ["o", "ओ", "ो"], ["u", "उ", "ु"],
];

const VIRAMA = "्";
const ANUSVARA = "ं";

const isVowelChar = (ch) => "aeiou".includes(ch);

/* Spelled out letter by letter, or read as a word? An all-caps run with no
   vowel inside it — IMS, DPE, HSDPR — is an acronym; one with a vowel in the
   middle — ANIL, KUMAR — is a name someone happened to type in caps, and
   spelling that out would be unreadable. */
function looksLikeAcronym(word) {
  /* A lone capital is an initial — "R. K. Gupta" — and reads as its letter
     name, never as the sound र. */
  if (/^[A-Z]$/.test(word)) return true;
  if (!/^[A-Z]{2,6}$/.test(word)) return false;
  const inner = word.slice(1, -1).toLowerCase();
  return ![...inner].some(isVowelChar);
}

/* One Roman word. Walked left to right, carrying one piece of state: whether
   the consonant just written is still waiting for its vowel. That is what
   decides between a matra and an independent vowel, and between a bare
   consonant and a half one. */
function word(raw) {
  const lower = raw.toLowerCase();
  const bare = lower.replace(/\.$/, "");
  if (HONORIFICS[bare]) return HONORIFICS[bare];
  if (WORDS[bare]) return WORDS[bare];
  if (looksLikeAcronym(raw)) {
    return [...lower].map((ch) => LETTER_NAMES[ch] ?? ch).join("");
  }

  let out = "";
  let pending = false;  // a consonant is written but has taken no vowel yet
  let i = 0;

  while (i < lower.length) {
    const rest = lower.slice(i);

    /* A final "y" is the vowel ī, not the consonant य — Chakarbarty,
       Tripathy, Chaudhary all end on the sound, never on a half य. */
    if (rest === "y" && pending) {
      out += "ी";
      pending = false;
      i += 1;
      continue;
    }

    const vowel = VOWELS.find(([roman]) => rest.startsWith(roman));
    if (vowel) {
      const [roman, independent, matra] = vowel;
      /* A word ending in "a" is long in practice — Sharma, Meena, Kavita all
         end in ā — even though the same letter is silent mid-word. */
      const finalA = roman === "a" && i + 1 === lower.length && out !== "";
      if (pending) out += finalA ? "ा" : matra;
      else out += finalA ? "आ" : independent;
      pending = false;
      i += roman.length;
      continue;
    }

    /* A nasal swallowed by the consonant after it — Anand, Sandeep, Shankar,
       Sinha. Only once the previous consonant has had its vowel: before that
       there is nothing for the dot to sit on. */
    const nasal = (lower[i] === "n" || lower[i] === "m")
      && !pending
      && out !== ""
      && i + 1 < lower.length
      && !isVowelChar(lower[i + 1]);
    if (nasal) {
      out += ANUSVARA;
      i += 1;
      continue;
    }

    const consonant = CONSONANTS.find(([roman]) => rest.startsWith(roman));
    if (consonant) {
      const [roman, glyph] = consonant;
      /* Two consonants with no vowel between them: the first one loses its
         inherent vowel and becomes a half letter. */
      if (pending) out += VIRAMA;
      out += glyph;
      pending = true;
      i += roman.length;
      continue;
    }

    /* Anything else — a digit, a stray mark — is carried across untouched. */
    out += lower[i];
    pending = false;
    i += 1;
  }

  return out;
}

/* Transliterate a whole value, leaving everything that isn't a Roman word
   (spaces, dots, digits, and anything already in Devanagari) where it is. */
export function toDevanagari(text) {
  if (!text) return text;
  return String(text).replace(/[A-Za-z]+/g, (w) => word(w));
}

export default toDevanagari;
